import { Link } from 'react-router-dom';
import {
  Upload,
  FileVideo,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { useContentList, useDraftsList } from '../hooks/useApi';
import { ContentStatusBadge, DraftStatusBadge } from '../components/common/StatusBadge';
import { PlatformBadge } from '../components/common/PlatformBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';

export function DashboardPage() {
  const { data: contentData, loading: contentLoading } = useContentList();
  const { data: draftsData, loading: draftsLoading } = useDraftsList();

  const isLoading = contentLoading || draftsLoading;

  const totalContent = contentData?.items.length ?? 0;
  const pendingApprovals = draftsData?.items.filter(
    (d) => d.status === 'draft' || d.status === 'in_review',
  ).length ?? 0;
  const approvedDrafts = draftsData?.items.filter(
    (d) => d.status === 'approved' || d.status === 'scheduled',
  ).length ?? 0;
  const publishedToday = draftsData?.items.filter((d) => {
    if (d.status !== 'published') return false;
    const today = new Date().toISOString().slice(0, 10);
    return d.updatedAt.startsWith(today);
  }).length ?? 0;

  const recentContent = (contentData?.items ?? []).slice(0, 5);
  const pendingDrafts = (draftsData?.items ?? [])
    .filter((d) => d.status === 'draft' || d.status === 'in_review')
    .slice(0, 5);

  if (isLoading) return <LoadingSpinner message="Loading dashboard..." />;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">
          Cream City Mortgage — Social Media Content Manager
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<FileVideo className="h-6 w-6" />}
          label="Total Content"
          value={totalContent}
          color="text-brand-blue bg-blue-50"
          linkTo="/library"
        />
        <StatCard
          icon={<AlertCircle className="h-6 w-6" />}
          label="Pending Approvals"
          value={pendingApprovals}
          color="text-yellow-600 bg-yellow-50"
          linkTo="/review"
          highlight={pendingApprovals > 0}
        />
        <StatCard
          icon={<CheckCircle className="h-6 w-6" />}
          label="Ready to Publish"
          value={approvedDrafts}
          color="text-green-600 bg-green-50"
          linkTo="/review"
        />
        <StatCard
          icon={<TrendingUp className="h-6 w-6" />}
          label="Published Today"
          value={publishedToday}
          color="text-emerald-600 bg-emerald-50"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Uploads */}
        <div className="card">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-gray-900">Recent Uploads</h2>
            <Link
              to="/library"
              className="flex items-center gap-1 text-xs font-medium text-brand-green hover:underline"
            >
              View All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {recentContent.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-gray-500">No content uploaded yet</p>
                <Link to="/upload" className="btn-primary mt-3 text-xs">
                  <Upload className="mr-1 h-3 w-3" />
                  Upload Content
                </Link>
              </div>
            ) : (
              recentContent.map((item) => (
                <Link
                  key={item.contentId}
                  to={`/content/${item.contentId}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50"
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                      item.mediaType === 'video'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}
                  >
                    <FileVideo className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(item.uploadedAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                  <ContentStatusBadge status={item.status} />
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="card">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-gray-900">
              Pending Approvals
              {pendingApprovals > 0 && (
                <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100 text-xs font-bold text-yellow-700">
                  {pendingApprovals}
                </span>
              )}
            </h2>
            <Link
              to="/review"
              className="flex items-center gap-1 text-xs font-medium text-brand-green hover:underline"
            >
              Review All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingDrafts.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-gray-500">All caught up! No pending approvals.</p>
              </div>
            ) : (
              pendingDrafts.map((draft) => (
                <Link
                  key={draft.draftId}
                  to={`/content/${draft.contentId}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50"
                >
                  <PlatformBadge platform={draft.platform} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-800">
                      {draft.textContent.slice(0, 80)}
                      {draft.textContent.length > 80 ? '...' : ''}
                    </p>
                  </div>
                  <DraftStatusBadge status={draft.status} />
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link
          to="/upload"
          className="card flex items-center gap-4 p-5 transition-shadow hover:shadow-md"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-green-50 text-brand-green">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Upload Content</h3>
            <p className="text-xs text-gray-500">Add new videos or images</p>
          </div>
        </Link>
        <Link
          to="/review"
          className="card flex items-center gap-4 p-5 transition-shadow hover:shadow-md"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-50 text-yellow-600">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Review Drafts</h3>
            <p className="text-xs text-gray-500">Approve or reject pending posts</p>
          </div>
        </Link>
        <Link
          to="/library"
          className="card flex items-center gap-4 p-5 transition-shadow hover:shadow-md"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-brand-blue">
            <FileVideo className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Content Library</h3>
            <p className="text-xs text-gray-500">Browse all uploaded content</p>
          </div>
        </Link>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  linkTo?: string;
  highlight?: boolean;
}

function StatCard({ icon, label, value, color, linkTo, highlight }: StatCardProps) {
  const content = (
    <div
      className={`card p-5 transition-shadow ${linkTo ? 'hover:shadow-md' : ''} ${
        highlight ? 'ring-2 ring-yellow-300' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}
